// User related types
export interface User {
  id: number;
  firstName?: string;
  lastName?: string;
  email: string;
  phone?: string;
  profileImageUrl?: string;
  gender?: number;
  isVerified: boolean;
  isBlocked: boolean;
  isDeleted: boolean;
  role?: string;
  flavourId?: number; // Keep for backward compatibility
  flavourIds?: number[]; // New field for multiple app assignments
  assignedTours?: number[];
  createdAt?: Date;
  updatedAt?: Date;
}

// Shared theme color palette
export interface ThemeColorPalette {
  // Brand colors
  PRIMARY?: string;
  PRIMARY_e0f5f2?: string;
  PRIMARY_DARK?: string;
  SECONDARY?: string;
  SECONDARY_300?: string;
  ACCENT?: string;
  BLUE_200?: string;
  BOTTOMTAB_ACTIVE?: string;

  // Whites
  WHITE?: string;
  WHITE_200?: string;
  WHITE_300?: string;
  WHITE_400?: string;
  WHITE_800?: string;
  WHITE_900?: string;
  WHITE_EEE?: string;
  WHITE_FDFDFD?: string;

  // Blacks
  BLACK?: string;
  BLACK_100?: string;
  BLACK_444?: string;
  BLACK_373737?: string;
  BLACK_222?: string;
  BLACK_0D?: string;
  BLACK_14?: string;
  BLACK_30726633?: string;
  BLACK_555555?: string;
  BLACK_24253D?: string;
  LIGHT_BLACK?: string;

  // Greys
  GREY?: string;
  GREY_2A2A2A?: string;
  GREY_505050?: string;
  DARK_GREY?: string;
  GRAY_100?: string;
  GRAY_300?: string;
  GRAY_374151?: string;
  GRAY_4C4C4C?: string;
  GRAY_505050?: string;
  GRAY_600?: string;
  GRAY_666?: string;
  GRAY_757575?: string;
  GRAY_777777?: string;
  GRAY_800?: string;
  GRAY_8E8E8E?: string;
  GRAY_9D9D9D?: string;
  GRAY_A1A1AA?: string;
  GRAY_AFAFAF1A?: string;
  GRAY_B8B8B8?: string;
  GRAY_C4C4C4?: string;
  GRAY_D1D1D1?: string;
  GRAY_D3D3D3?: string;
  GRAY_D4D4D4?: string;
  GRAY_D8D8D8?: string;
  GRAY_DDD?: string;
  GRAY_E8E8E8?: string;
  GRAY_E9E9E9?: string;
  GRAY_ECECEC?: string;

  // Colors
  GREEN_007B64?: string;
  GREEN_4CAF50?: string;
  BLUE_00796B?: string;
  BLUE_11B5AE66?: string;
  BLUE_1976D2?: string;
  BLUE_2196F3?: string;
  BLUE_2C7D6B?: string;
  BLUE_2E4E9D?: string;
  BLUE_32344C?: string;
  BLUE_3629B7?: string;
  BLUE_42C7B1?: string;
  BLUE_4A9B8E?: string;
  BLUE_83D0BF?: string;
  BLUE_87CEEB?: string;

  // Backgrounds & accents
  CREAME_WHITE?: string;
  BACKGROUND_WHITE?: string;
  BEIGE?: string;
  MIRAGE?: string;
  TRANSPARENT?: string;

  // Highlights
  RED?: string;
  RED_FF474C?: string;
  RED_FF3D00?: string;
  GOLD?: string;
  GOLD_FACC15?: string;
  GOLD_FFC107?: string;
  GOLD_FFAD08?: string;
}

// Auth related types
export interface LoginResponse {
  success: boolean;
  message: string;
  data?: {
    userId?: number;
    adminId?: number;
    email: string;
    firstName?: string;
    lastName?: string;
    role?: string;
    authToken?: string;
    requiresPasswordSetup?: boolean;
  };
}

// Auth Context types
export interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
  currentFlavourId: string | null;
  setCurrentFlavourId: (flavourId: string) => void;
  availableFlavours: Flavour[];
  refreshFlavours: () => Promise<void>;
  flavourChangeTrigger: number;
}

// Tour Point related types
export interface TourPoint {
  id: number;
  tourId: number;
  name: string;
  mainAudioUrl: string;
  audioUrl: string;
  thumbnailUrl: string;
  radius: number;
  lattitude: string;
  longitude: string;
  discription: string;
  audioLock: string;
  status: string;
  isDeleted: string;
  createdAt: string;
  updatedAt: string;
  isHidePreview: string;
  isHideStop: string;
  stopLanguagesMap: string;
  stopLanguagesPreviewMap: string;
  order?: number;
}

// Tour type enum
export enum TourType {
  WALKING = 'walking',
  DRIVING = 'driving',
}

export const TourTypeLabels = {
  [TourType.WALKING]: 'Walking Tour',
  [TourType.DRIVING]: 'Driving Tour',
} as const;

// Tour related types
export interface Tour {
  id: number;
  userId: number;
  productId?: string; // Made optional
  name: string; // Only name, no title
  imageUrl: string;
  thumbnailUrl: string;
  type: TourType; // Changed to enum
  duration: string;
  step: string;
  description: string;
  retailPrice: string;
  sellingPrice: string;
  rating: string;
  reviewCount: string;
  distance: string;
  carRentalPerPerson: string;
  workMode: string;
  narrationPoints: string;
  place: string;
  countryId?: number;
  placeId?: number;
  // bookingEmailTemplate: string;
  tourDirectionJsonUrl?: string; // Renamed from tourDirectionUrl
  status: string;
  isDeleted: boolean;
  createdAt: Date;
  updatedAt: Date;
  flavourId: string;
  tourPointsDetail?: TourPoint[];
}

// Bundle related types
export interface Bundle {
  id: number;
  productId?: string;
  bokunId?: string;
  name: string;
  description?: string;
  place?: string;
  countryId?: number;
  placeId?: number;
  bundleEmailTemplate?: string;
  retailPrice?: string;
  sellingPrice?: number;
  status?: string;
  imageUrl?: string;
  isDeleted: boolean;
  createdAt: Date;
  updatedAt: Date;
  flavourId: string;
  // Related data
  bundelDetails?: Array<{
    id: number;
    bundelId: number;
    tourId: number;
    status: number;
    isDeleted: number;
    tour?: {
      id: number;
      name: string;
      description: string;
      imageUrl?: string;
      status: string;
    };
  }>;
}

// Bundle form data for creation/editing
export interface BundleFormData {
  name: string;
  description?: string;
  place?: string;
  countryId?: number;
  placeId?: number;
  productId?: string;
  bokunId?: string;
  bundleEmailTemplate?: string;
  sellingPrice?: number;
  retailPrice?: string;
  status?: string;
  imageUrl?: string;
  flavourId?: string;
  tourIds?: number[];
}

// Payment related types
export interface Payment {
  id: number;
  userId: number;
  tourId: string;
  amount: number;
  currency: string;
  paymentMethod: string;
  status: number;
  transactionId: string;
  isDeleted: number;
  createdAt: Date;
  updatedAt: Date;
}

// Booking related types
export interface Booking {
  id: number;
  userId: number;
  tourId?: number; // Optional for bundle bookings
  bundleId?: number; // Optional for individual tour bookings
  purchaseType: 'playstore' | 'appstore' | 'bokun' | 'redeem'; // bokun = redeem from Bokun, redeem = same as bokun
  bookingDate: Date;
  redeemCount?: number;
  totalRedeems?: number;
  status: 'active' | 'used' | 'expired' | 'cancelled';
  transactionId?: string;
  amount?: number;
  currency?: string;
  isDeleted: boolean;
  createdAt: Date;
  updatedAt: Date;
  flavourId: string;
  // Related data
  user?: User;
  tour?: Tour;
  bundle?: Bundle;
}

// Review related types
export interface Review {
  id: number;
  userId: number;
  tourId: number;
  userName: string;
  rating: number;
  reviewTitle: string;
  review: string;
  reviewDate: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  imageUrl: string[];
  createdAt: string;
  updatedAt: string;
  tour?: {
    id: number;
    name: string;
  } | null;
  user?: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
  } | null;
  flavour?: {
    id: number;
    name: string;
  } | null;
}



// Dashboard related types
export interface DashboardStats {
  totalUsers: number;
  totalTours: number;
  totalRevenue: number;
  totalBookings: number;
  revenueData: Array<{ name: string; value: number }>;
  toursVsUsers: Array<{ name: string; value: number }>;
  tourStatusDistribution: Array<{ name: string; value: number }>;
}

// API Response types
export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
  statusCode?: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Flavour/App related types
export interface Flavour {
  id: string;
  name: string;
  description?: string;
  packageName?: string;
  platform?: 'ios' | 'android' | 'both';
  appIconUrl?: string;
  title?: string;
  onboardBgUrl?: string;
  homeBgUrl?: string;
  themeColors?: ThemeColorPalette;
  status?: 'active' | 'review' | 'draft' | 'inactive' | 'suspended';
  stepsDone?: string;
  
  // Firebase Configuration fields
  keystoreSha1?: string;
  keystoreSha256?: string;
  googleServicesJsonUrl?: string;
  googleServicesPlistUrl?: string;
  googleClientApiAndroid?: string;
  googleClientApiIos?: string;
  mobileProvisionName?: string;
  mobileProvisionUUID?: string;
  
  // Keystore fields
  keystoreUrl?: string;
  keystorePassword?: string;
  keystoreAlias?: string;
  
  // Build related fields
  pipelineId?: string;
  iosPipelineId?: string;
  buildId?: string;
  buildStatus?: 'pending' | 'running' | 'completed' | 'failed';
  iosBuildId?: string;
  iosBuildStatus?: 'pending' | 'running' | 'completed' | 'failed';

  // Version fields
  androidVersionCode?: string;
  androidVersionName?: string;
  iosVersionCode?: string;
}

// Form types
export interface LoginFormData {
  email: string;
  password: string;
}

export interface FlavourFormData {
  name: string;
  description: string;
  packageName: string;
  appIcon?: File; // Optional for edit mode when not updating the icon
  title?: string;
  onboardBg?: File;
  homeBg?: File;
  pipelineId?: string;
  iosPipelineId?: string;
  buildId?: string;
  buildStatus?: 'pending' | 'running' | 'completed' | 'failed';
  iosBuildId?: string;
  iosBuildStatus?: 'pending' | 'running' | 'completed' | 'failed';
  themeColors?: ThemeColorPalette;
  platform?: 'ios' | 'android' | 'both';
  stepsDone?: string;
  googleServicesJson?: File;
  googleServicesPlist?: File;
  googleClientApiAndroid?: string;
  googleClientApiIos?: string;
  mobileProvision?: File;
  mobileProvisionName?: string;
  mobileProvisionUUID?: string;
  // Keystore fields (from separate keystore generation)
  keystoreUrl?: string;
  keystoreSha1?: string;
  keystoreSha256?: string;
  keystorePassword?: string;
  keystoreAlias?: string;
  keystoreBuffer?: string; // Base64 encoded keystore buffer
  steps_done?: 'step_1' | 'step_2' | 'step_3' | 'step_4';
  publish_status?: 'draft' | 'save';
  androidVersionCode?: string;
  androidVersionName?: string;
  iosVersionCode?: string;
}

// Admin user types
export interface AdminUserFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: 'super_admin' | 'admin' | 'sub_admin';
  permissions: string[];
  flavourId?: number;
  createdBy?: number; // ID of the user who created this admin
}

// Admin User type (extends User with admin-specific fields)
export interface AdminUser extends User {
  role: 'super_admin' | 'admin' | 'sub_admin';
  permissions: string[];
  lastLoginAt?: Date;
  isActive: boolean;
  flavourId?: number;
  createdBy?: number; // ID of the user who created this admin
  flavour?: {
    id: number;
    name: string;
    slug: string;
    status: string;
  };
} 